/**
 * The picker, grouped by client (T2.12).
 *
 * An admin sees every client; everyone else sees only the clients they hold a
 * grant under, which the API decides — this screen renders what it is given and
 * filters nothing, so there is no second, weaker copy of the access rules here.
 *
 * Every noun on this screen comes from useTerms(). The word "Blueprint" does not
 * appear in this file.
 */
import { Link } from 'react-router';
import { resolveTerms } from '@blueprint/shared';
import { useBlueprints, groupByClient, type BlueprintSummary, type ClientRef } from '../lib/blueprints';
import { TermsProvider, useTerms } from '../lib/terms';
import { useDocumentTitle } from '../lib/documentTitle';

function Row({ blueprint }: { blueprint: BlueprintSummary }) {
  const terms = useTerms();
  const updated = new Date(blueprint.updatedAt);
  const updatedLabel = Number.isNaN(updated.getTime())
    ? null
    : updated.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <li className="pick-row">
      <Link className="pick-link" to={`/blueprints/${blueprint.id}`}>
        <span className="pick-name">{blueprint.name}</span>
        <span className="pick-meta">
          {blueprint.latestVersionId
            ? `Live version available${updatedLabel ? ` · updated ${updatedLabel}` : ''}`
            : `No published version yet — this ${terms.noun('blueprint')} has not been released.`}
        </span>
      </Link>
    </li>
  );
}

/**
 * One client's group, in that client's own words.
 *
 * This screen spans several clients, so there is no single resolved terminology
 * for the page as a whole — each group gets its own. Where the API has not sent
 * a client override the resolution falls through to the system defaults, which
 * is the correct answer rather than a fallback.
 *
 * The page heading, the tab bar and document.title are deliberately NOT inside
 * any of these providers: they describe a list that crosses clients, so no one
 * client's vocabulary is right for them. Raised with the Lead — see the report
 * for Phase 2.
 */
function ClientGroup({
  client,
  blueprints,
}: {
  client: ClientRef;
  blueprints: BlueprintSummary[];
}) {
  return (
    <TermsProvider terminology={resolveTerms(client.terminology, null)}>
      <section className="pick-group">
        <h2 className="pick-client">{client.name}</h2>
        <ul className="pick-list">
          {blueprints.map((b) => (
            <Row blueprint={b} key={b.id} />
          ))}
        </ul>
      </section>
    </TermsProvider>
  );
}

export function BlueprintsRoute() {
  const terms = useTerms();
  const { data, isPending, isError, error } = useBlueprints();

  useDocumentTitle(terms.plural('blueprint'));

  const groups = data ? groupByClient(data) : [];

  return (
    <div className="sheet">
      <div className="masthead">
        <div>
          <h1>{terms.plural('blueprint')}</h1>
          <p className="meta">Choose a {terms.noun('blueprint')} to open.</p>
        </div>
      </div>

      {isPending ? (
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      ) : isError ? (
        <div className="empty" role="alert">
          Could not load your {terms.plural('blueprint')}.
          <br />
          {error instanceof Error ? error.message : 'Try again in a moment.'}
        </div>
      ) : groups.length === 0 ? (
        <div className="empty">
          You have not been given access to any {terms.plural('blueprint')} yet. Ask an
          administrator to grant you access.
        </div>
      ) : (
        <div className="pick-groups">
          {groups.map((group) => (
            <ClientGroup client={group.client} blueprints={group.blueprints} key={group.client.id} />
          ))}
        </div>
      )}
    </div>
  );
}
